import { useCallback, useState } from 'react';

import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { RefreshIcon } from '@twilio-paste/icons/esm/RefreshIcon';

import { ApiKeyType, useRegenerateApiKey } from '../../../../modules/Apps';
import { ConfirmationDialog, useConfirmationDialog } from '../../../common/ConfirmationDialog';
import { InfoLabel } from '../../../common/InfoLabel';
import { AppSecret } from '../AppSecretsDrawer/AppSecret';

interface RegenerateAppSecretProps {
  label: string;
  appId: string;
  infoText: string;
  apiKeyType: ApiKeyType;
}

export function RegenerateAppSecret({
  appId,
  label,
  apiKeyType,
  infoText,
}: RegenerateAppSecretProps) {
  const [newApiKey, setNewApiKey] = useState<string>('');

  const { mutateAsync, isPending } = useRegenerateApiKey();

  const { isOpen, handleOpen, handleClose } = useConfirmationDialog();

  const regenerateKey = useCallback(async () => {
    const { apiPrimaryKey = '', apiSecondaryKey = '' } = await mutateAsync({ appId, apiKeyType });
    setNewApiKey(apiKeyType === ApiKeyType.PrimaryKey ? apiPrimaryKey : apiSecondaryKey);
    handleClose();
  }, [apiKeyType, appId, handleClose, mutateAsync]);

  return (
    <>
      <Box marginBottom="space50">
        <InfoLabel htmlFor="" infoText={infoText}>
          {label}
        </InfoLabel>
        <Box display="flex" columnGap="space40">
          <Button variant="secondary" onClick={handleOpen}>
            <RefreshIcon decorative />
            Regenerate
          </Button>
          <AppSecret apiKey={newApiKey} />
        </Box>
      </Box>
      <ConfirmationDialog
        isOpen={isOpen}
        confirmButtonLabel="Confirm"
        heading="Regenerate API Key"
        text="Are you sure you want to regenerate the API key? Once you click “confirm,” the previous API Key will no longer be valid."
        handleConfirm={regenerateKey}
        handleDismiss={handleClose}
        destructive
        isConfirmDisabled={isPending}
      ></ConfirmationDialog>
    </>
  );
}
